// src/layout/builders/ERP/ERPAttendanceTab.jsx
// ERP-level Attendance Tab: shows all projects with Employee / Contractor sub-tabs
import React, { useEffect, useState, useCallback } from "react";
import useAuthStore from "../../../store/useAuthStore";
import useERPStore from "../../../store/builders/useERPStore";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import {
  Users, HardHat, RefreshCw,
  CalendarDays, ArrowRight, CheckCircle, RotateCcw,
  History, Wallet, Calendar as CalendarIcon, X,
  Briefcase, ArrowLeft, Eye,
  Building2, Search, Filter, ChevronDown
} from "lucide-react";

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString("en-IN")}`;

const TYPE_COLOR = {
  Tile: "bg-amber-50 text-amber-700 border-amber-100",
  Brick: "bg-red-50 text-red-700 border-red-100",
  Plumbing: "bg-blue-50 text-blue-700 border-blue-100",
  Electrical: "bg-yellow-50 text-yellow-700 border-yellow-100",
  Carpentry: "bg-orange-50 text-orange-700 border-orange-100",
  Painting: "bg-pink-50 text-pink-700 border-pink-100",
  Mason: "bg-stone-50 text-stone-700 border-stone-100",
  Other: "bg-gray-50 text-gray-700 border-gray-100",
};

// ── Employee Full Analytics Panel ─────────────────────────────────────────────
const EmployeeAnalyticsPanel = ({ emp, builderId, onClose }) => {
  const [data, setData] = useState({ records: [], totalEarned: 0, totalPaid: 0, balance: 0 });
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [payInput, setPayInput] = useState({ id: null, amount: "" });

  const load = useCallback(async (m) => {
    setLoading(true);
    const [full, hist] = await Promise.all([
      useERPStore.getState().fetchEmployeeAttendanceFull(emp.employee_id, m),
      useERPStore.getState().fetchSalaryHistory(emp.employee_id),
    ]);
    setData(full);
    setSalaryHistory(hist);
    setLoading(false);
  }, [emp.employee_id]);

  const handlePay = async (logId, amount) => {
    setPayingId(logId);
    const ok = await useERPStore.getState().markAttendancePaid(logId, amount);
    if (ok) {
      load(month);
      setPayInput({ id: null, amount: "" });
    }
    setPayingId(null);
  };

  useEffect(() => { load(month); }, []);

  const handleMonthChange = (e) => {
    setMonth(e.target.value);
    load(e.target.value);
  };

  return (
    <div className="bg-white rounded-xl border border-blue-200 shadow-sm overflow-hidden">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border-b border-blue-100">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs border border-blue-200">
            {emp.name?.charAt(0).toUpperCase()}
          </div>
          <span className="font-bold text-gray-800 text-sm">{emp.name}'s Attendance</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex items-center">
            <CalendarIcon size={13} className="absolute left-2 text-gray-400 pointer-events-none" />
            <input
              type="month"
              value={month}
              onChange={handleMonthChange}
              className="pl-7 pr-2 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded-lg outline-none focus:border-blue-500 cursor-pointer"
            />
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-white" title="Close">
            <X size={15} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center">
          <div className="animate-spin rounded-full h-7 w-7 border-2 border-blue-200 border-t-blue-600"></div>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { label: "Present", val: data.records.filter(r => r.status === "present").length, unit: "days", cls: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
              { label: "Half Day", val: data.records.filter(r => r.status === "half_day").length, unit: "days", cls: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
              { label: "Absent", val: data.records.filter(r => r.status === "absent").length, unit: "days", cls: "text-rose-700", bg: "bg-rose-50 border-rose-200" },
              { label: "Total Earned", val: fmt(data.totalEarned), cls: "text-blue-800", bg: "bg-blue-50 border-blue-200" },
            ].map(({ label, val, unit, cls, bg }) => (
              <div key={label} className={`${bg} border rounded-xl p-3 flex flex-col gap-0.5`}>
                <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
                <p className={`text-lg font-black ${cls}`}>{val}{unit ? <span className="text-[9px] font-semibold ml-0.5 opacity-60">{unit.toUpperCase()}</span> : ""}</p>
              </div>
            ))}
          </div>

          {/* Paid / Balance */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2">
              <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Paid</p>
                <p className="text-base font-black text-emerald-800">{fmt(data.totalPaid)}</p>
              </div>
            </div>
            <div className={`rounded-xl p-3 border flex items-center gap-2 ${data.balance > 0 ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"}`}>
              <Wallet size={20} className={data.balance > 0 ? "text-amber-500 flex-shrink-0" : "text-gray-400 flex-shrink-0"} />
              <div>
                <p className={`text-[10px] font-bold uppercase tracking-wider ${data.balance > 0 ? "text-amber-600" : "text-gray-500"}`}>Balance Due</p>
                <p className={`text-base font-black ${data.balance > 0 ? "text-amber-800" : "text-gray-600"}`}>{fmt(data.balance)}</p>
              </div>
            </div>
          </div>

          {/* Salary history */}
          {salaryHistory.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-3 py-2 border-b bg-gray-50 flex items-center gap-1.5">
                <History size={12} className="text-gray-400" />
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Salary History</span>
              </div>
              {salaryHistory.map((h, i) => (
                <div key={h.id} className="px-3 py-2 flex items-center justify-between text-xs border-b last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-100 rounded font-bold uppercase">
                      {i === 0 ? "Latest" : "Prev"}
                    </span>
                    <span className="text-gray-500 font-medium">
                      From {new Date(h.effective_from).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  <span className="font-bold text-gray-800">{fmt(h.daily_salary)}/day</span>
                </div>
              ))}
            </div>
          )}

          {/* Attendance log table */}
          {data.records.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b text-[10px] uppercase text-gray-500 font-bold tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Project</th>
                      <th className="text-right px-3 py-2">Earned</th>
                      <th className="text-right px-3 py-2">Pay</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.records.map(log => (
                      <tr key={log.id} className="hover:bg-gray-50/50">
                        <td className="px-3 py-2 font-bold text-gray-700">
                          {new Date(log.attendance_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                        </td>
                        <td className="px-3 py-2">
                          {log.status === "present"
                            ? <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold px-1.5 py-0.5 rounded">P</span>
                            : log.status === "half_day"
                              ? <span className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold px-1.5 py-0.5 rounded">H</span>
                              : <span className="bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-bold px-1.5 py-0.5 rounded">A</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-500 truncate max-w-[120px]">{log.project_name || "—"}</td>
                        <td className="px-3 py-2 text-right font-bold text-gray-700">{log.day_earning > 0 ? fmt(log.day_earning) : "—"}</td>
                        <td className="px-3 py-2 text-right">
                          {log.payment_status === "paid" ? (
                            <div className="flex flex-col items-end gap-0.5">
                              <span className="bg-emerald-50 text-emerald-600 border border-emerald-100 text-[9px] font-bold px-1.5 py-0.5 rounded">Paid</span>
                              {log.paid_amount != null && (
                                <span className="text-[9px] text-emerald-700 font-bold">{fmt(log.paid_amount)}</span>
                              )}
                            </div>
                          ) : log.status !== "absent" ? (
                            payInput.id === log.id ? (
                              <div className="flex items-center gap-1 justify-end">
                                <span className="text-[10px] text-gray-400 font-bold">₹</span>
                                <input
                                  type="number"
                                  value={payInput.amount}
                                  onChange={e => setPayInput(p => ({ ...p, amount: e.target.value }))}
                                  className="w-20 text-xs font-bold text-gray-700 border border-amber-300 rounded px-1.5 py-0.5 outline-none focus:border-amber-500"
                                  placeholder="Amount"
                                  autoFocus
                                />
                                <button
                                  onClick={() => handlePay(log.id, payInput.amount)}
                                  disabled={payingId !== null || !payInput.amount}
                                  className="bg-amber-500 text-white text-[9px] font-bold px-2 py-0.5 rounded hover:bg-amber-600 transition-colors disabled:opacity-60"
                                >
                                  {payingId === log.id ? "..." : "✓"}
                                </button>
                                <button
                                  onClick={() => setPayInput({ id: null, amount: "" })}
                                  className="text-gray-400 hover:text-gray-600 text-[9px] font-bold px-1 py-0.5"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setPayInput({ id: log.id, amount: String(log.day_earning || "") })}
                                disabled={payingId !== null}
                                className="bg-amber-50 text-amber-600 border border-amber-200 text-[9px] font-bold px-2 py-0.5 rounded hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-colors disabled:opacity-60 cursor-pointer"
                              >
                                Pay
                              </button>
                            )
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data.records.length === 0 && (
            <div className="py-6 text-center text-gray-400 text-xs font-medium bg-gray-50 rounded-xl border border-dashed border-gray-200">
              No attendance records for {month}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Contractor Timeline Card ──────────────────────────────────────────────────
const ContractorTimelineCard = ({ c, onPaySuccess }) => {
  const [payInput, setPayInput] = useState({ open: false, amount: "" });
  const [paying, setPaying] = useState(false);

  const handleContractorPay = async () => {
    const amount = parseFloat(payInput.amount);
    if (!amount || amount <= 0) return;
    setPaying(true);
    const ok = await useERPStore.getState().payContractorAmount(c.id, amount);
    if (ok) {
      setPayInput({ open: false, amount: "" });
      if (onPaySuccess) onPaySuccess();
    }
    setPaying(false);
  };

  const status = c.contract_status || "active";
  const fmtD = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const statusBadge = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    completed: "bg-blue-50 text-blue-700 border-blue-200",
    default: "bg-gray-50 text-gray-600 border-gray-200"
  }[status] || "bg-gray-50 text-gray-600 border-gray-200";

  const statusLabel = { active: "Active", completed: "Completed" }[status] || status;

  const cardBorder = {
    active: "border-gray-200",
    completed: "border-blue-200 bg-blue-50/20",
  }[status] || "border-gray-200";

  return (
    <div className={`bg-white rounded-xl border ${cardBorder} shadow-sm overflow-hidden`}>
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm border-2 flex-shrink-0 ${
              status === "completed" ? "bg-blue-100 text-blue-700 border-blue-200" :
              "bg-orange-100 text-orange-600 border-orange-200"
            }`}>
              {(c.name || "C").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-gray-800 text-sm">{c.name}</p>
                <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase ${TYPE_COLOR[c.contractor_type] || "bg-gray-50 text-gray-700 border-gray-100"}`}>
                  {c.contractor_type}
                </span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase ${statusBadge}`}>
                  {statusLabel}
                </span>
              </div>
              <p className="text-[10px] text-gray-400 font-medium mt-0.5 truncate">
                {c.company_name || (() => {
                  const w = (c.name || "").trim().split(/\s+/);
                  return w.length >= 2 ? w.slice(1).map(x => x[0].toUpperCase()).join("") : (c.name || "").slice(0, 2).toUpperCase();
                })()}
              </p>
            </div>
          </div>
          {c.contract_amount && (
            <div className="bg-orange-50 border border-orange-100 rounded-lg px-3 py-1.5 flex-shrink-0 text-right">
              <p className="text-[9px] font-bold text-orange-400 uppercase tracking-wider">Contract</p>
              <p className="text-sm font-black text-orange-700">{fmt(c.contract_amount)}</p>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              <CalendarDays size={11} /> Timeline
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-md text-xs font-bold text-emerald-700">
                {c.start_date ? new Date(c.start_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
              </span>
              <ArrowRight size={12} className="text-gray-300" />
              {status === "completed" && c.completed_date ? (
                <span className="bg-blue-50 border border-blue-200 px-2 py-1 rounded-md text-xs font-bold text-blue-700 flex items-center gap-1">
                  <CheckCircle size={10} /> {new Date(c.completed_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                </span>
              ) : (
                <span className={`px-2 py-1 rounded-md text-xs font-bold ${c.end_date ? "bg-gray-100 border border-gray-200 text-gray-600" : "bg-gray-50 border border-dashed border-gray-200 text-gray-400"}`}>
                  {c.end_date ? new Date(c.end_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "Ongoing"}
                </span>
              )}
            </div>
          </div>
        </div>

        {status === "completed" && (
          <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg w-fit">
            <CheckCircle size={12} className="text-blue-600" />
            <span className="text-xs font-bold text-blue-700">
              Completed {c.completed_date ? `on ${new Date(c.completed_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
            </span>
          </div>
        )}

        {/* Payment section */}
        {c.contract_amount && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Paid</p>
                  <p className="text-sm font-black text-emerald-700">{fmt(c.paid_amount || 0)}</p>
                </div>
                <div>
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Balance</p>
                  <p className={`text-sm font-black ${(parseFloat(c.contract_amount) - parseFloat(c.paid_amount || 0)) > 0 ? "text-amber-600" : "text-gray-400"}`}>
                    {fmt(parseFloat(c.contract_amount) - parseFloat(c.paid_amount || 0))}
                  </p>
                </div>
              </div>
              {(parseFloat(c.contract_amount) - parseFloat(c.paid_amount || 0)) > 0 && (
                payInput.open ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-gray-400 font-bold">₹</span>
                    <input
                      type="number"
                      value={payInput.amount}
                      onChange={e => setPayInput(p => ({ ...p, amount: e.target.value }))}
                      className="w-24 text-xs font-bold text-gray-700 border border-amber-300 rounded px-1.5 py-1 outline-none focus:border-amber-500"
                      placeholder="Amount"
                      autoFocus
                    />
                    <button
                      onClick={handleContractorPay}
                      disabled={paying || !payInput.amount}
                      className="bg-amber-500 text-white text-[9px] font-bold px-2 py-1 rounded hover:bg-amber-600 transition-colors disabled:opacity-60"
                    >
                      {paying ? "..." : "✓"}
                    </button>
                    <button
                      onClick={() => setPayInput({ open: false, amount: "" })}
                      className="text-gray-400 hover:text-gray-600 text-[9px] font-bold px-1 py-1"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setPayInput({ open: true, amount: "" })}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-600 border border-amber-200 rounded-lg text-xs font-bold hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-all"
                  >
                    Pay
                  </button>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Project Detail View ───────────────────────────────────────────────────────
const ProjectDetailView = ({ project, builderId, onBack }) => {
  const [employees, setEmployees] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsEmp, setAnalyticsEmp] = useState(null);
  const [innerTab, setInnerTab] = useState("employees");
  const [searchQuery, setSearchQuery] = useState("");
  const [positionFilter, setPositionFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, contrRes] = await Promise.all([
        axiosInstancev1.get(`/erp/assignments/project/${project.id}?builderId=${builderId}`),
        axiosInstancev1.get(`/erp/contractor-assignments/project/${project.id}?builderId=${builderId}`),
      ]);
      const allWorkers = empRes.data?.data || [];
      setEmployees(allWorkers.filter(w => w.worker_type !== "contractor"));
      setContractors(contrRes.data?.data || []);
    } catch (e) { }
    setLoading(false);
  }, [project.id, builderId]);

  useEffect(() => { load(); }, [load]);

  // ── Full-screen Employee Analytics View ──────────────────────────────────
  if (analyticsEmp) {
    return (
      <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
        {/* Breadcrumb back */}
        <div className="bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
          <button
            onClick={() => setAnalyticsEmp(null)}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-blue-600 transition-colors"
            title="Back to employees"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={onBack}
              className="text-gray-400 hover:text-blue-600 font-medium transition-colors"
            >
              {project.project_name}
            </button>
            <span className="text-gray-300">/</span>
            <button
              onClick={() => setAnalyticsEmp(null)}
              className="text-gray-400 hover:text-blue-600 font-medium transition-colors"
            >
              Employees
            </button>
            <span className="text-gray-300">/</span>
            <span className="font-bold text-gray-800">{analyticsEmp.name}</span>
          </div>
        </div>
        <EmployeeAnalyticsPanel
          emp={analyticsEmp}
          builderId={builderId}
          onClose={() => setAnalyticsEmp(null)}
        />
      </div>
    );
  }

  // ── Tab navigation ────────────────────────────────────────────────────────
  const renderTabNav = () => (
    <div className="flex gap-1.5 bg-gray-100 p-1 rounded-lg w-fit">
      {[
        { key: "employees", label: "Employees", icon: Users, count: employees.length },
        { key: "contractors", label: "Contractors", icon: HardHat, count: contractors.length },
      ].map(({ key, label, icon: Icon, count }) => {
        const active = innerTab === key;
        return (
          <button
            key={key}
            onClick={() => setInnerTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-bold transition-all ${active ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"}`}
          >
            <Icon size={13} />
            {label}
            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${active ? "bg-gray-100 text-gray-600" : "bg-white text-gray-400"}`}>
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );

  // ── Tab content ───────────────────────────────────────────────────────────
  const renderContent = () => {
    if (innerTab === "employees") {
      const uniquePositions = [...new Set(employees.map(e => e.position || "Staff"))].filter(Boolean);
      const filteredEmployees = employees.filter(emp => {
        const matchesSearch = (emp.name || "").toLowerCase().includes(searchQuery.toLowerCase());
        const matchesPosition = positionFilter === "all" || 
                                (emp.position || "Staff").toLowerCase() === positionFilter.toLowerCase();
        return matchesSearch && matchesPosition;
      });

      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-sm flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Search size={16} /></div>
              <input
                type="text"
                placeholder="Search employee name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500"
              />
            </div>
            <div className="flex-shrink-0 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Filter size={16} /></div>
              <select
                value={positionFilter}
                onChange={(e) => setPositionFilter(e.target.value)}
                className="pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500 bg-white appearance-none cursor-pointer font-medium text-gray-700"
              >
                <option value="all">All Positions</option>
                {uniquePositions.map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"><ChevronDown size={14} /></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            {filteredEmployees.length === 0 ? (
              <div className="p-12 text-center flex flex-col items-center gap-3">
                <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center text-gray-300">
                  <Users size={24} />
                </div>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                  {employees.length === 0 ? "No employees assigned to this project" : "No employees match your search"}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredEmployees.map(emp => (
                  <div
                    key={emp.employee_id || emp.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50/40 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-white border border-blue-100 text-blue-700 flex items-center justify-center font-black text-xs shadow-sm flex-shrink-0">
                        {(emp.name || "E").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <button
                          onClick={() => setAnalyticsEmp(emp)}
                          className="font-semibold text-blue-600 hover:text-blue-800 hover:underline text-left text-sm font-bold focus:outline-none"
                          title="Click to view full details"
                        >
                          {emp.name}
                        </button>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider flex items-center gap-1 mt-0.5">
                          <Briefcase size={10} />
                          {emp.position || "Staff"} &bull; {fmt(emp.daily_salary)}/day
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setAnalyticsEmp(emp)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 border border-blue-100 rounded-lg text-xs font-bold hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all"
                    >
                      <Eye size={12} /> View Analytics
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {contractors.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center flex flex-col items-center gap-3 shadow-sm">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center text-gray-300">
              <HardHat size={24} />
            </div>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">No contractors assigned to this project</p>
          </div>
        ) : (
          contractors.map(c => <ContractorTimelineCard key={c.id} c={c} onPaySuccess={load} />)
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Detail Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-blue-600 transition-colors"
            title="Back to projects"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shadow-sm flex-shrink-0">
            <Building2 size={22} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">{project.project_name}</h2>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 font-bold uppercase tracking-wider">
              <span className="flex items-center gap-1">
                <Users size={11} />
                {loading ? "—" : employees.length} Employee{employees.length !== 1 ? "s" : ""}
              </span>
              <span className="text-gray-200">|</span>
              <span className="flex items-center gap-1">
                <HardHat size={11} />
                {loading ? "—" : contractors.length} Contractor{contractors.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-xl text-xs font-bold hover:bg-gray-100 transition border border-gray-200"
        >
          <RotateCcw size={13} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      {renderTabNav()}

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-slate-100 border-t-blue-600"></div>
          <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Loading workforce data...</p>
        </div>
      ) : (
        renderContent()
      )}
    </div>
  );
};

// ── Status Filter Config ───────────────────────────────────────────────────────
const STATUS_FILTERS = [
  { key: "active", label: "Live", dotColor: "bg-emerald-500" },
  { key: "hold", label: "Hold", dotColor: "bg-amber-400" },
  { key: "completed", label: "Completed", dotColor: "bg-blue-500" },
];

// ── Main ERPAttendanceTab ─────────────────────────────────────────────────────
const ERPAttendanceTab = () => {
  const { user } = useAuthStore();
  const builderId = user?.builderId || user?.builder_id || user?.id;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [selectedProject, setSelectedProject] = useState(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstancev1.get(`/projects?builderId=${builderId}&limit=100&page=1`);
      setProjects(res.data?.projects || []);
    } catch (e) { }
    setLoading(false);
  }, [builderId]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  if (selectedProject) {
    return (
      <ProjectDetailView
        project={selectedProject}
        builderId={builderId}
        onBack={() => setSelectedProject(null)}
      />
    );
  }

  const statusColors = {
    active: "bg-emerald-100 text-emerald-700 border-emerald-200",
    completed: "bg-blue-100 text-blue-700 border-blue-200",
    hold: "bg-amber-100 text-amber-700 border-amber-200",
    default: "bg-gray-100 text-gray-600 border-gray-200",
  };

  const filteredProjects = projects.filter(p => (p.status || "").toLowerCase() === statusFilter);
  const countByStatus = (key) => projects.filter(p => (p.status || "").toLowerCase() === key).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
            <Users size={18} />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-800">Workforce Attendance</h2>
            <p className="text-xs text-gray-500 font-medium">
              {projects.length} Project{projects.length !== 1 ? "s" : ""} · Click a project to view attendance
            </p>
          </div>
        </div>
        <button
          onClick={loadProjects}
          className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 transition"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-1.5 bg-gray-100 p-1 rounded-lg w-fit flex-wrap">
        {STATUS_FILTERS.map(({ key, label, dotColor }) => {
          const active = statusFilter === key;
          const count = countByStatus(key);
          return (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${active ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"}`}
            >
              <span className={`w-2 h-2 rounded-full ${dotColor} inline-block`}></span>
              {label}
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${active ? "bg-gray-100 text-gray-600" : "bg-white text-gray-400"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500 font-medium px-1">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>Present</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block"></span>Half Day</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500 inline-block"></span>Absent</span>
        <span className="flex items-center gap-1.5 ml-auto text-[10px]">
          <span className="bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded font-bold uppercase">
            Click employee to expand attendance analytics
          </span>
        </span>
      </div>

      {/* Projects Table */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-slate-100 border-t-blue-600"></div>
          <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Fetching projects...</p>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-16 text-center flex flex-col items-center gap-4 shadow-sm">
          <div className="w-14 h-14 rounded-full bg-slate-50 flex items-center justify-center text-slate-300">
            <Users size={28} />
          </div>
          <p className="text-slate-800 font-black uppercase tracking-wider text-sm">No {STATUS_FILTERS.find(f => f.key === statusFilter)?.label || statusFilter} Projects Found</p>
          <p className="text-slate-400 text-xs font-medium">Try adjusting your filters or check your connection.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
                  <th className="text-left px-4 py-3">Project Name</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProjects.map((p, i) => {
                  const sKey = (p.status || "").toLowerCase();
                  const sCls = statusColors[sKey] || statusColors.default;
                  return (
                    <tr key={p.id || i} className="hover:bg-gray-50/40 transition-colors">
                      <td className="px-4 py-4">
                        <div className="font-semibold text-gray-800">{p.project_name}</div>
                        {p.location && (
                          <div className="text-[10px] text-gray-400 mt-0.5">{p.location}</div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`text-[10px] px-2 py-0.5 rounded border font-bold uppercase ${sCls}`}>
                          {p.status || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={() => setSelectedProject(p)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 border border-blue-100 rounded-lg text-xs font-bold hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all"
                        >
                          <Eye size={13} /> View Workspace
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPAttendanceTab;
